import { sum } from 'lib/array/arrayUtils'
import { clamp, floatify } from 'lib/maths/mathUtils'

export const FLIGHT_COMMISSION_MAX_PERCENTAGE = 50
export const FLIGHT_COMMISSION_FINE_STEP = 0.25
export const FLIGHT_COMMISSION_MID_STEP = 0.5
export const FLIGHT_COMMISSION_COARSE_STEP = 1
export const FLIGHT_COMMISSION_FINE_MAX = 2
export const FLIGHT_COMMISSION_MID_MAX = 20

const ADD_ON_FEE_KEYS = ['flightSelectedSeatsTotal', 'flightBaggageTotal', 'flightBaggageAddonTotal'] as const
const TAX_FEE_KEYS = ['flightBookingFee', 'flightServiceFee', 'flightCreditFee', 'flightAtolFee'] as const

function sumFeeKeys(
  otherFees: App.Checkout.FlightFees | undefined,
  keys: ReadonlyArray<keyof App.Checkout.FlightFees>,
): number {
  if (!otherFees) return 0
  return floatify(sum([...keys], (key) => otherFees[key] ?? 0))
}

export function getFlightCommissionStep(percentage: number): number {
  if (percentage < FLIGHT_COMMISSION_FINE_MAX) return FLIGHT_COMMISSION_FINE_STEP
  if (percentage < FLIGHT_COMMISSION_MID_MAX) return FLIGHT_COMMISSION_MID_STEP
  return FLIGHT_COMMISSION_COARSE_STEP
}

export function snapFlightCommissionPercentage(value: number): number {
  const clamped = clamp(0, value, FLIGHT_COMMISSION_MAX_PERCENTAGE)
  const step = getFlightCommissionStep(clamped)
  return floatify(Math.round(clamped / step) * step)
}

export function getFlightAddOnFees(otherFees?: App.Checkout.FlightFees): number {
  return sumFeeKeys(otherFees, ADD_ON_FEE_KEYS)
}

function sumJourneyFareExcludingTax(journeys: Array<App.AgentHubFlightCommissionJourneyPrice>): number {
  return Math.max(0, sum(journeys, (journey) => (journey.totalFare ?? 0) - (journey.totalTax ?? 0)))
}

function getRoundTripFare(journeys: Array<App.AgentHubFlightCommissionJourneyPrice>): number {
  const journeyWithRoundTrip = journeys.find((journey) => (journey.totalRoundTripPrice ?? 0) > 0)
  if (!journeyWithRoundTrip) {
    return sumJourneyFareExcludingTax(journeys)
  }

  return Math.max(
    0,
    (journeyWithRoundTrip.totalRoundTripPrice ?? 0) - (journeyWithRoundTrip.totalRoundTripTax ?? 0),
  )
}

export function getFlightCommissionPriceParts({
  journeys = [],
  otherFees,
  fareType,
}: App.AgentHubFlightCommissionPricePartsInput = {}): App.AgentHubFlightCommissionPriceParts {
  const addOns = getFlightAddOnFees(otherFees)
  const otherTaxFees = sumFeeKeys(otherFees, TAX_FEE_KEYS)
  const totalTax = sum(journeys, (journey) => journey.totalTax ?? 0)
  const totalFees = sum(journeys, (journey) => journey.totalFees ?? 0)
  const usesRoundTripFare = fareType === 'return' || fareType === 'multiCity'
  const fare = usesRoundTripFare ? getRoundTripFare(journeys) : sumJourneyFareExcludingTax(journeys)

  return {
    fare: floatify(fare),
    addOns,
    taxesAndFees: floatify(totalTax + totalFees + otherTaxFees),
  }
}

export function getFlightCommissionTotals(
  parts: App.AgentHubFlightCommissionPriceParts,
  percentage: number,
): App.AgentHubFlightCommissionTotals {
  const snappedPercentage = snapFlightCommissionPercentage(percentage)
  const base = floatify(parts.fare + parts.addOns)
  const earned = floatify(base * (snappedPercentage / 100))
  const fullPrice = floatify(base + parts.taxesAndFees)

  return {
    ...parts,
    base,
    earned,
    fullPrice,
    customerTotal: floatify(fullPrice + earned),
  }
}
